import {
  AhaEditorUtils,
  type DocumentValue,
  HtmlSerializer,
  MigrateToV2,
  slate,
  type AhaEditor,
  type EditorPlugin,
} from '@aha-app/aha-editor-v2';
import { isEqual } from 'lodash';

export const EDITOR_SET_TEMPLATE_EVENT = 'aha-editor-v2:set-template';

interface TemplateContent {
  body: string;
  editor_content?: DocumentValue;
}

export type SetTemplateEvent = CustomEvent<{
  target: string;
  template: string | TemplateContent;
  lastTemplate?: string | TemplateContent;
}>;

interface EditorTemplateSelectionPluginOptions {
  target: string;
}

declare global {
  interface WindowEventMap {
    EDITOR_SET_TEMPLATE_EVENT: SetTemplateEvent;
  }
}

// This plugin provides an interface for an external input to switch out a template for the note.
// This replaces the v1 editor's "setTemplate" functionality.
export function EditorTemplateSelectionPluginFactory(
  options: EditorTemplateSelectionPluginOptions
): EditorPlugin {
  const { target } = options;
  let ahaEditor: AhaEditor;

  function setTemplate(event: SetTemplateEvent) {
    if (event.detail?.target !== target) {
      return;
    }

    const lastTemplate = event.detail.lastTemplate;

    let lastTemplateFragment: DocumentValue | null = null;
    if (
      lastTemplate &&
      typeof lastTemplate !== 'string' &&
      lastTemplate.editor_content
    ) {
      lastTemplateFragment = MigrateToV2(lastTemplate.editor_content);
    }

    // We will not update the editor if the user edited the content manually
    if (
      AhaEditorUtils.Editor.isEmpty(ahaEditor.editor) ||
      !lastTemplateFragment ||
      isEqual(lastTemplateFragment.children, ahaEditor.editor.children)
    ) {
      const template: TemplateContent =
        typeof event.detail.template === 'string'
          ? { body: event.detail.template }
          : event.detail.template;

      let fragment: DocumentValue | null = null;

      if (template.editor_content) {
        fragment = MigrateToV2(template.editor_content);
      } else {
        const serializer = new HtmlSerializer(
          ahaEditor.editor,
          ahaEditor.controller.serializerOptions
        );

        fragment = {
          children: serializer.deserialize(template.body).children,
        };
      }

      AhaEditorUtils.Selection.selectAll(ahaEditor.editor);
      slate.Transforms.removeNodes(ahaEditor.editor, { mode: 'highest' });
      slate.Transforms.insertFragment(ahaEditor.editor, fragment.children);
    }
  }

  return {
    name: 'EditorTemplateSelection plugin',
    handlers: {
      onInit(_ahaEditor) {
        ahaEditor = _ahaEditor;

        window.addEventListener(EDITOR_SET_TEMPLATE_EVENT, setTemplate);
      },

      onDestroy() {
        window.removeEventListener(EDITOR_SET_TEMPLATE_EVENT, setTemplate);
      },
    },
  };
}
